import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import { styled } from "@mui/material/styles";
import { SelectElement, TextareaAutosizeElement, PasswordElement, TextFieldElement, DatePickerElement, CheckboxElement, TimePickerElement, MultiSelectElement } from "react-hook-form-mui";
const breakpoints = {
    values: {
        xs: 0,
        sm: 500, // changed only this one from the default 600
        md: 900,
        lg: 1200,
        xl: 1536
    }
};
const components = {
    MuiInputBase: {
        styleOverrides: {
            root: {
                background: '#fff',
                fontSize: '1.25rem'
            }
        }
    },
    MuiTypography: {
        styleOverrides: {
            "h6": {
                color: 'rgba(0, 0, 0, 0.87)',
                flexGrow: 1,
                fontWeight: 700,
                fontSize: '1.5rem',
                marginTop: '32px',
                marginBottom: '16px'
            },
            "subtitle1": {
                color: 'rgba(0, 0, 0, 0.87)',
                fontSize: '1.5rem',
                fontWeight: 500,
            }
        }
    },
    MuiButton: {
        styleOverrides: {
            root: {
                fontSize: '1.25rem',
                textTransform: 'none'
            },
            "contained": {},
            "outlined": {
                background: '#fff',
                "&:hover": {
                    background: '#1976d2',
                    color: '#fff'
                }
            }
        }
    },
    MuiDivider: {
        styleOverrides: {
            root: {
                marginTop: '48px',
                marginBottom: '24px' // because this is almost always followed by a header with a top margin of 24
            }
        }
    },
    MuiFormLabel: {
        styleOverrides: {
            root: {
                fontSize: '1.25rem'
            }
        }
    }
};
const typography = {
    fontFamily: [
        "'DM Sans'",
        'sans-serif',
    ].join(','),
};
export const lightTheme = {
    breakpoints: breakpoints,
    components: components,
    typography: typography,
    palette: {
        mode: "light",
        text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            disabled: 'rgba(0, 0, 0, 0.38)',
        },
        action: {
            active: 'rgba(0, 0, 0, 0.54)',
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(0, 0, 0, 0.08)',
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)'
        },
        background: {
            default: "#fff",
            paper: '#fafafa',
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
            contrastText: '#fcf'
        },
        secondary: {
            main: '#9c27b0',
            light: '#ba68c8',
            dark: '#7b1fa2',
            contrastText: '#fef'
        }
    }
};
export const darkTheme = {
    breakpoints: breakpoints,
    components: Object.assign(Object.assign({}, components), {
        MuiInputBase: {
            styleOverrides: {
                root: {
                    background: "#121212",
                    fontSize: '1.25rem'
                }
            }
        },
        MuiTypography: {
            styleOverrides: {
                "h6": {
                    color: '#fff',
                    flexGrow: 1,
                    fontWeight: 700,
                    fontSize: '1.75rem',
                    marginTop: '32px',
                    marginBottom: '16px'
                },
                "subtitle1": {
                    color: '#fff',
                    mx: 1,
                    my: 2,
                    fontSize: '1.5rem',
                    fontWeight: 500,
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                    textTransform: 'none'
                },
                "outlined": {
                    background: "#121212",
                    "&:hover": {
                        background: '#fff',
                        color: "#121212"
                    }
                }
            }
        },
    }),
    typography: typography,
    palette: {
        mode: 'dark',
        text: {
            primary: '#fff',
            secondary: 'rgba(255,255,255,0.7)',
            disabled: 'rgba(255,255,255,0.5)'
        },
        action: {
            active: '#fff',
            hover: 'rgba(255,255,255,0.0.8)',
            selected: 'rgba(255,255,255,0.16)',
            disabled: 'rgba(255,255,255,0.3)',
            disabledBackground: 'rgba(255,255,255,0.06)'
        },
        background: {
            default: "#121212",
            paper: '#151515'
        },
        divider: 'rgba(255,255,255,0.12)',
        primary: {
            main: '#90caf9',
            light: '#e3f2fd',
            dark: '#42a5f5',
            contrastText: 'rgba(0, 0, 0, 0.87)'
        },
        secondary: {
            main: '#ce93d8',
            light: '#f3e5f5',
            dark: '#ab47bc',
            contrastText: 'rgba(0, 0, 0, 0.87)'
        }
    }
};
export const contrastTheme = {
    breakpoints: breakpoints,
    components: components,
    typography: typography,
    palette: {
        mode: 'light',
        text: {
            primary: 'rgba(0, 0, 0, 0.87)',
            secondary: 'rgba(0, 0, 0, 0.6)',
            disabled: 'rgba(0, 0, 0, 0.38)',
        },
        action: {
            active: 'rgba(0, 0, 0, 0.54)',
            hover: 'rgba(0, 0, 0, 0.04)',
            selected: 'rgba(0, 0, 0, 0.08)',
            disabled: 'rgba(0, 0, 0, 0.26)',
            disabledBackground: 'rgba(0, 0, 0, 0.12)'
        },
        background: {
            default: "#fff",
            paper: '#fafafa'
        },
        divider: 'rgba(0, 0, 0, 0.12)',
        primary: {
            main: '#1976d2',
            light: '#42a5f5',
            dark: '#1565c0',
            contrastText: '#fff'
        },
        secondary: {
            main: '#9c27b0',
            light: '#ba68c8',
            dark: '#7b1fa2',
            contrastText: '#fff'
        }
    }
};
export const BoxContainer = styled(Box)((props) => ({
    // maxWidth: isMobile ? '100vw' : '650px'
    maxWidth: props.orientation === 'horizontal' ? '100%' : '500px',
    padding: '0 16px 64px 16px'
}));
export const CustomTextFieldElement = styled(TextFieldElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { borderColor: theme.palette.text.secondary, backgroundColor: theme.palette.background.default, "& fieldset": {
        borderColor: theme.palette.text.secondary
    }, "& .Mui-focused": {
        borderColor: theme.palette.text.primary,
        color: `${theme.palette.text.primary} !important`
    }, "& .MuiInputBase-colorPrimary": {
        backgroundColor: theme.palette.background.default
    }, "& .MuiFormLabel-colorPrimary.Mui-error": {
        color: theme.palette.text.primary
    }, margin: '8px 0', "& input": {
        padding: "25px 14px 16.5px 14px",
        color: theme.palette.text.primary,
        "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`
        },
    }, "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 20px) scale(1)" // translate(14px, 16px) scale(1);
    }, "& .MuiInputLabel-shrink": {
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, -9px) scale(0.75);
    }, "& .MuiOutlinedInput-notchedOutline": {
        top: 0,
        "& legend": {
            display: 'none'
        }
    } })));
export const CustomShrinkTextFieldElement = styled(TextFieldElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { borderColor: theme.palette.text.secondary, "& fieldset": {
        borderColor: theme.palette.text.secondary
    }, "& .Mui-focused": {
        borderColor: theme.palette.text.primary,
        color: `${theme.palette.text.primary} !important`
    }, margin: '8px 0', "& input": {
        padding: "25px 14px 16.5px 14px",
        "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`
        }
    }, "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, 16px) scale(1);
    }, "& .MuiInputLabel-shrink": {
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, -9px) scale(0.75);
    }, "& .MuiOutlinedInput-notchedOutline": {
        top: 0,
        "& legend": {
            display: 'none'
        }
    } })));
export const CustomTextField = styled(TextField)(({ theme }) => (Object.assign({}, theme.typography.body2)));
export const CustomSelectElement = styled(SelectElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { borderColor: theme.palette.text.secondary, backgroundColor: theme.palette.background.default, "& fieldset": {
        borderColor: theme.palette.text.secondary
    }, "& .Mui-focused": {
        borderColor: theme.palette.text.primary,
        color: `${theme.palette.text.primary} !important`
    }, margin: '8px', width: 'calc(100% - 16px)', "& label": {
        color: theme.palette.text.primary
    }, "& svg": {
        color: theme.palette.text.primary
    }, "& .MuiInputBase-formControl": {
        backgroundColor: theme.palette.background.default,
    }, "& .MuiSelect-select": {
        padding: "25px 14px 16.5px 14px"
    }, "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 20px) scale(1)" // translate(14px, 16px) scale(1);
    }, "& .MuiInputLabel-shrink": {
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, -9px) scale(0.75);
    }, "& .MuiOutlinedInput-notchedOutline": {
        top: 0,
        "& legend": {
            display: 'none'
        }
    } })));
export const CustomMultiSelectElement = styled(MultiSelectElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { backgroundColor: theme.palette.background.default })));
export const CustomAutocomplete = styled(Autocomplete)(({ theme }) => (Object.assign({}, theme.typography.body2)));
export const CustomDatePickerElement = styled(DatePickerElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { borderColor: theme.palette.text.secondary, "& fieldset": {
        borderColor: theme.palette.text.secondary
    }, "& .Mui-focused": {
        borderColor: theme.palette.text.primary,
        color: `${theme.palette.text.primary} !important`
    }, margin: '8px 0', "& input": {
        padding: "25px 14px 16.5px 14px",
        "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`
        }
    }, "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 20px) scale(1)" // translate(14px, 16px) scale(1);
    }, "& .MuiInputLabel-shrink": {
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, -9px) scale(0.75);
    }, "& .MuiOutlinedInput-notchedOutline": {
        top: 0,
        "& legend": {
            display: 'none'
        }
    } })));
export const CustomTimePickerElement = styled(TimePickerElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { borderColor: theme.palette.text.secondary, "& fieldset": {
        borderColor: theme.palette.text.secondary
    }, margin: '8px 0', "& input": {
        padding: "25px 14px 16.5px 14px",
        "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`
        }
    }, "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 20px) scale(1)" // translate(14px, 16px) scale(1);
    }, "& .MuiInputLabel-shrink": {
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, -9px) scale(0.75);
    }, "& .MuiOutlinedInput-notchedOutline": {
        top: 0,
        "& legend": {
            display: 'none'
        }
    } })));
export const CustomTextareaAutosizeElement = styled(TextareaAutosizeElement)(({ theme }) => (Object.assign(Object.assign({}, theme.typography.body2), { backgroundColor: theme.palette.background.default, borderColor: theme.palette.text.secondary, "& fieldset": {
        borderColor: theme.palette.text.secondary
    }, "& .MuiInputBase-colorPrimary": {
        backgroundColor: theme.palette.background.default
    }, "& .Mui-focused": {
        borderColor: theme.palette.text.primary,
        color: `${theme.palette.text.primary} !important`
    }, margin: '8px 0', "& textarea": {
        padding: "10px 0 0 0",
        "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.palette.background.default} inset`
        }
    }, "& .MuiInputLabel-formControl:not(.MuiInputLabel-shrink)": {
        transform: "translate(14px, 20px) scale(1)" // translate(14px, 16px) scale(1);
    }, "& .MuiInputLabel-shrink": {
        color: theme.palette.text.primary,
        transform: "translate(14px, 4px) scale(0.75)" // translate(14px, -9px) scale(0.75);
    }, "& .MuiOutlinedInput-notchedOutline": {
        top: 0,
        "& legend": {
            display: 'none'
        }
    } })));
export const CustomPasswordElement = styled(PasswordElement)(({ theme }) => (Object.assign({}, theme.typography.body2)));
export const CustomCheckboxElement = styled(CheckboxElement)(({ theme }) => (Object.assign({}, theme.typography.body2)));
